import { useCallback, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import useMount from 'react-use/esm/useMount';

import { logger } from 'modules/logger';
import { worklist } from 'modules/analytics/constants';

import { activeCustomWorklistFiltersState, activeCustomWorklistIDsState } from '../state';
import { useCustomWorklistLoadingContext } from '../contexts/CustomWorklistLoadingContext';
import { useUpdateCustomWorklistFilters } from './useUpdateCustomWorklistFilters';
import { useCustomWorklists } from './useCustomWorklists';
import { hasSameItems } from '../utils/worklistUtils';
import { getCustomWorklistNames } from '../WorklistPageV2/utils';
import { buildFilterDetails } from '../../analytics/utils';
import { WorklistMode, WorklistTypeDetail, WorklistTypeDetailType } from '../../analytics/state';
import { trackWorklistAction } from '../../analytics/useWorklistAnalytics';
import { type FilterStateType } from '../../WorklistFilters/types';
import { WithApolloContext } from 'modules/Apollo/types';
import { describeFilters } from 'domains/worklist/WorklistFilters/describeFilters';

type useFetchDefaultCustomWorklistFiltersProps = WithApolloContext & {
  defaultCustomWorklistIDs: string[];
  replaceFilters: (filters: FilterStateType) => void;
  skipQueries?: boolean;
};

type UseFetchDefaultCustomWorklistFiltersReturn = {
  fetchDefaultCustomWorklistFilters: (defaultWorklistIDs: string[]) => Promise<void>;
};

/**
 * Hook to fetch default custom worklist filters on mount.
 * This handles the initial loading of worklist filters and analytics.
 *
 * @param defaultCustomWorklistIDs - Array of worklist IDs to load by default
 * @param replaceFilters - Function to replace the current filters with new ones
 * @returns Object containing the fetchDefaultCustomWorklistFilters function
 */
export const useFetchDefaultCustomWorklistFilters = ({
  defaultCustomWorklistIDs,
  replaceFilters,
  context,
  skipQueries = false,
}: useFetchDefaultCustomWorklistFiltersProps): UseFetchDefaultCustomWorklistFiltersReturn => {
  const { allWorklists, isPublicOrCombinedWorklist } = useCustomWorklists({
    queryOptions: { context, skip: skipQueries },
  });
  const { isProcessingFiltersRef } = useCustomWorklistLoadingContext();
  const { updateSingleCustomWorklistFilters, updateCombinedCustomWorklistFilters } =
    useUpdateCustomWorklistFilters({ replaceFilters, context, skipQueries });

  const [activeCustomWorklistIDs, setActiveCustomWorklistIDs] = useRecoilState(
    activeCustomWorklistIDsState
  );
  const activeCustomWorklistFilters = useRecoilValue(activeCustomWorklistFiltersState);

  const hasMountedRef = useRef(false);

  const trackDefaultWorklistLoad = useCallback(
    (worklistIDs: string[], appliedFilters: Nullable<FilterStateType>) => {
      try {
        const worklistTypes = new Set<WorklistTypeDetailType>();

        for (const worklistID of worklistIDs) {
          const worklist = allWorklists.get(worklistID);
          if (worklist?.internalSettings?.isCanned) {
            worklistTypes.add(WorklistTypeDetail.Canned);
            continue;
          }

          if (isPublicOrCombinedWorklist([worklistID])) {
            worklistTypes.add(WorklistTypeDetail.Public);
          } else {
            worklistTypes.add(WorklistTypeDetail.Private);
          }
        }

        const types = Array.from(worklistTypes).sort();
        const mode = worklistIDs.length > 1 ? WorklistMode.Combined : WorklistMode.Single;
        const worklistNames = getCustomWorklistNames(worklistIDs, allWorklists);

        trackWorklistAction(worklist.usr.loadDefaultWorklist, {
          worklistSmids: worklistIDs,
          worklistNames,
          ...(appliedFilters ? buildFilterDetails(appliedFilters) : {}),
          worklistTypes: types,
          worklistMode: mode,
        });
      } catch (error) {
        logger.error(
          '[useFetchDefaultCustomWorklistFilters] Error tracking loadDefaultWorklist analytics',
          error,
          {
            filters: describeFilters(appliedFilters),
            worklistIDs,
          }
        );
      }
    },
    [allWorklists, isPublicOrCombinedWorklist]
  );

  const fetchDefaultCustomWorklistFilters = useCallback(
    async (defaultWorklistIDs: string[]) => {
      if (hasMountedRef.current) return;
      hasMountedRef.current = true;

      if (
        defaultWorklistIDs.length === 0 ||
        (activeCustomWorklistFilters != null &&
          activeCustomWorklistIDs.length > 0 &&
          hasSameItems(activeCustomWorklistIDs, defaultWorklistIDs))
      ) {
        return;
      }

      logger.info(
        `[useFetchDefaultCustomWorklistFilters] Fetching default custom worklist filters for: ${defaultWorklistIDs.join(', ')}`
      );

      isProcessingFiltersRef.current = true;

      // Set active custom worklist IDs if not already set
      if (activeCustomWorklistIDs.length === 0) {
        setActiveCustomWorklistIDs(defaultWorklistIDs);
        logger.info(
          `[useFetchDefaultCustomWorklistFilters] Set activeCustomWorklistIDs to ${defaultWorklistIDs.join(', ')}`
        );
      }

      try {
        let appliedFilters: Nullable<FilterStateType> = null;

        if (defaultWorklistIDs.length > 1) {
          logger.info(
            `[useFetchDefaultCustomWorklistFilters] Loading combined worklists: ${defaultWorklistIDs.join(', ')}`
          );
          appliedFilters = await updateCombinedCustomWorklistFilters(defaultWorklistIDs);
        } else {
          const customWorklistID = defaultWorklistIDs[0] ?? null;
          logger.info(
            `[useFetchDefaultCustomWorklistFilters] Loading single worklist: ${customWorklistID}`
          );
          appliedFilters = await updateSingleCustomWorklistFilters(customWorklistID);
        }

        if (appliedFilters) {
          trackDefaultWorklistLoad(defaultWorklistIDs, appliedFilters);
          replaceFilters(appliedFilters);
        }

        logger.info(
          `[useFetchDefaultCustomWorklistFilters] Successfully loaded default worklist filters for: ${defaultWorklistIDs.join(', ')}`
        );
      } catch (error) {
        logger.error(
          '[useFetchDefaultCustomWorklistFilters] Error in fetchDefaultCustomWorklistFilters',
          error,
          { defaultWorklistIDs }
        );
      } finally {
        isProcessingFiltersRef.current = false;
      }
    },
    [
      activeCustomWorklistFilters,
      activeCustomWorklistIDs,
      isProcessingFiltersRef,
      replaceFilters,
      setActiveCustomWorklistIDs,
      trackDefaultWorklistLoad,
      updateCombinedCustomWorklistFilters,
      updateSingleCustomWorklistFilters,
    ]
  );

  useMount(() => {
    if (defaultCustomWorklistIDs.length > 0) {
      fetchDefaultCustomWorklistFilters(defaultCustomWorklistIDs);
    }
  });

  return {
    fetchDefaultCustomWorklistFilters,
  };
};
